import { z } from 'zod'
import { isResolved } from '../../../netlify/functions/lib/bug-management/statuses'
import { parseBugKinds } from '../../../netlify/functions/lib/bug-management/kind-filter'
import { summarizeBugs } from './bugSummary'
import { sessionBugData } from './testRunFindings'

export const ProjectBugIndexSchema = z.object({
  bugs: z.array(z.object({
    id: z.string(), title: z.string(), severity: z.string(), status: z.string(),
    polish_category: z.string().nullable(), test_run_id: z.string().nullable(),
    environment_id: z.string().nullable(), discovered_at: z.string(), replay_recording_id: z.string().nullable(),
    memberships: z.array(z.object({
      test_session_id: z.string().nullable(), version_id: z.string().nullable(), test_run_id: z.string().nullable(), is_duplicate: z.boolean(),
    })),
  })),
})
export type ProjectBugIndex = z.infer<typeof ProjectBugIndexSchema>

/** One complete index, rather than a page of bug reports, owns all project/run counts. */
export function projectBugIndexView(index: ProjectBugIndex) {
  const summary = summarizeBugs(index.bugs)
  const categories = new Map<string, { category: string; critical: number; high: number; medium: number; low: number; total: number }>()
  for (const bug of index.bugs) {
    const category = bug.polish_category ?? 'testing'
    const row = categories.get(category) ?? { category, critical: 0, high: 0, medium: 0, low: 0, total: 0 }
    row.total++
    if (bug.severity === 'critical' || bug.severity === 'high' || bug.severity === 'medium' || bug.severity === 'low') row[bug.severity]++
    categories.set(category, row)
  }
  type Finding = Omit<ProjectBugIndex['bugs'][number], 'memberships'> & { is_duplicate: boolean; version_id: string | null }
  const associations: Record<string, Map<string, Finding>> = {}
  const versions: Record<string, Map<string, Finding>> = {}
  function add(groups: Record<string, Map<string, Finding>>, scope: string, finding: Finding) {
    const group = groups[scope] ??= new Map()
    const key = JSON.stringify([finding.id, finding.test_run_id])
    const previous = group.get(key)
    if (!previous || (previous.is_duplicate && !finding.is_duplicate)) group.set(key, finding)
  }
  for (const { memberships, ...bug } of index.bugs) {
    for (const membership of memberships) {
      const finding = { ...bug, test_run_id: membership.test_run_id, is_duplicate: membership.is_duplicate, version_id: membership.version_id }
      if (membership.test_session_id) add(associations, membership.test_session_id, finding)
      if (membership.version_id) add(versions, membership.version_id, finding)
    }
  }
  return {
    ...index,
    breakdown: [...categories.values()].sort((a, b) => b.total - a.total),
    counts: {
      total: summary.total, openCount: summary.open, resolvedCount: summary.resolved,
      journeyBugs: index.bugs.filter(bug => bug.polish_category === null).length,
      polishBugs: index.bugs.filter(bug => bug.polish_category !== null).length,
    },
    versions: Object.fromEntries(Object.entries(versions).map(([id, bugs]) => [id, sessionBugData({ bugs: [...bugs.values()] })])),
    sessions: Object.fromEntries(Object.entries(associations).map(([id, bugs]) => [id, sessionBugData({ bugs: [...bugs.values()] })])),
  }
}

export function filteredBugIndexCounts(index: ProjectBugIndex, filters: {
  status?: string; hideResolved?: boolean; severity?: string; kind?: string; environment?: string
}) {
  const kinds = parseBugKinds(filters.kind)
  const bugs = index.bugs.filter(bug =>
    (!filters.severity || filters.severity === 'all' || bug.severity === filters.severity) &&
    (!filters.environment || filters.environment === 'all' ||
      (filters.environment === 'project-default' ? bug.environment_id === null : bug.environment_id === filters.environment)) &&
    (!kinds.length || (bug.polish_category !== null && kinds.includes(bug.polish_category)) ||
      (kinds.includes('testing') && bug.polish_category === null && bug.test_run_id !== null)))
  return {
    total: bugs.filter(bug => filters.status && filters.status !== 'all'
      ? bug.status === filters.status : !filters.hideResolved || !isResolved(bug.status)).length,
    resolvedCount: bugs.filter(bug => isResolved(bug.status)).length,
  }
}
